import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { ClavaContext } from "../../../config/contexts";
import { AppDispatch } from "../../../store";
import { selectMatchDays, selectSportsType } from "../../../selectors";
import {
  formatRelativeDateLabel,
  formatYmdLocal,
  isFiniteDate,
  parseYmdLocal,
  toLocalMidnight
} from "../../../config/utils";
import { pathHomeDate, toYmd } from "../../../config/routes";
import DateNavigator from "../DateNavigator";

const EMPTY_YMD_LIST: ReadonlyArray<string> = Object.freeze([]);

type UrlContext = {
  currentLeagueId?: string;
  currentDateYmd?: string;
};


const DateSelector: React.FC = () => {
  const { l, aoi } = useContext(ClavaContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const params = useParams();
  const { currentDateYmd } = useMemo<UrlContext>(
    () => ({ currentDateYmd: params.date }),
    [params.date]
  );

  // no-op

  const selectedDate = useMemo(() => {
    if (currentDateYmd && /^\d{4}-\d{2}-\d{2}$/.test(currentDateYmd)) {
      const [year, month, day] = currentDateYmd
        .split("-")
        .map((n) => parseInt(n, 10));
      const parsedDate = new Date(year, month - 1, day);
      if (isFiniteDate(parsedDate)) return toLocalMidnight(parsedDate);
    }
    return toLocalMidnight(new Date());
  }, [currentDateYmd]);

  const selectedYmd = useMemo(() => formatYmdLocal(selectedDate), [selectedDate]);

  // no-op

  const matchDaysRaw = useSelector(
    selectMatchDays
  );

  const matchDays = useMemo(
    () =>
      [...matchDaysRaw]
        .map((d) => toLocalMidnight(new Date(d)))
        .filter((d) => isFiniteDate(d))
        .sort((a, b) => a.getTime() - b.getTime()),
    [matchDaysRaw]
  );

  const visibleDaysYmd = useMemo(() => {
    if (!matchDays || matchDays.length === 0) return EMPTY_YMD_LIST;
    const set = new Set<string>();
    matchDays.forEach((d) => set.add(formatYmdLocal(d)));
    return Array.from(set.values()).sort();
  }, [matchDays]);

  const visibleDaysYmdSet = useMemo(() => new Set(visibleDaysYmd), [visibleDaysYmd]);
  const isActiveYmd = useCallback((d: Date) => visibleDaysYmdSet.has(formatYmdLocal(d)), [visibleDaysYmdSet]);

  // no-op

  const sports = useSelector(selectSportsType);

  const redirectInProgressRef = useRef<boolean>(false);
  const lastRedirectTargetRef = useRef<string | null>(null);
  const lastLoadedMonthKeyRef = useRef<string | null>(null);
  const lastHandledPath = useRef<string | null>(null);
  const requestedInitialDaysRef = useRef<boolean>(false);

  const label = useMemo(
    () => formatRelativeDateLabel(selectedDate, l),
    [selectedDate, l]
  );

  const goTo = useCallback((d: Date) => {
    const ymdDash = toYmd(d);
    navigate(pathHomeDate(ymdDash));
  }, [navigate]);

  const onDayPress = useCallback(
    (date: Date) => {
      if (date && isActiveYmd(date)) {
        goTo(date);
      }
    },
    [isActiveYmd, goTo]
  );

  useEffect(() => {
    lastLoadedMonthKeyRef.current = null;
    requestedInitialDaysRef.current = false;
    redirectInProgressRef.current = false;
    lastRedirectTargetRef.current = null;
    lastHandledPath.current = null;
  }, [aoi, sports]);

  const pickClosestTarget = useCallback((): string | null => {
    if (visibleDaysYmd.length === 0) return null;
    const next = visibleDaysYmd.find((d) => d >= selectedYmd);
    const prev = [...visibleDaysYmd].reverse().find((d) => d <= selectedYmd);
    return next || prev || null;
  }, [visibleDaysYmd, selectedYmd]);

  const redirectIfNeeded = useCallback((target: string | null, path: string) => {
    if (!target || target === selectedYmd) return false;
    if (lastRedirectTargetRef.current === target && redirectInProgressRef.current) return false;
    lastHandledPath.current = path;
    lastRedirectTargetRef.current = target;
    redirectInProgressRef.current = true;
    const dt = parseYmdLocal(target) ?? toLocalMidnight(new Date());
    goTo(dt);
    return true;
  }, [goTo, selectedYmd]);

  useEffect(() => {
    const path = location.pathname;
    if (lastHandledPath.current === path) return;
    const hasSelected = visibleDaysYmd.includes(selectedYmd);
    if (hasSelected) return;

    const isOnHomePath = path.startsWith("/home");
    if (isOnHomePath) return;

    const isOnSpecificDate =
      /^\/home\/date\/\d{4}-\d{2}-\d{2}$/.test(path);
    if (isOnSpecificDate) return;

    const currentTodayYmd = formatYmdLocal(toLocalMidnight(new Date()));
    if (selectedYmd === currentTodayYmd && !hasSelected) {
      if (visibleDaysYmd.length > 0) {
        const target = pickClosestTarget();
        if (redirectIfNeeded(target, path)) return;
      }
    }

    if (visibleDaysYmd.length > 0) {
      const target = pickClosestTarget();
      if (redirectIfNeeded(target, path)) return;
    }
    if (requestedInitialDaysRef.current) return;
    requestedInitialDaysRef.current = true;
  }, [
    visibleDaysYmd,
    selectedYmd,
    navigate,
    aoi,
    dispatch,
    sports,
    location.pathname,
    pickClosestTarget,
    redirectIfNeeded
  ]);

  useEffect(() => {
    if (visibleDaysYmd.includes(selectedYmd)) {
      redirectInProgressRef.current = false;
      lastRedirectTargetRef.current = null;
      lastHandledPath.current = null;
      requestedInitialDaysRef.current = false;
    }
  }, [visibleDaysYmd, selectedYmd]);

  const onPrev = () => {
    if (visibleDaysYmd.length > 0) {
      const prev = [...visibleDaysYmd].reverse().find((d) => d < selectedYmd);
      if (prev) {
        const dt = parseYmdLocal(prev) ?? toLocalMidnight(new Date());
        goTo(dt);
      }
    }
  };

  const onNext = () => {
    if (visibleDaysYmd.length > 0) {
      const next = visibleDaysYmd.find((d) => d > selectedYmd);
      if (next) {
        const dt = parseYmdLocal(next) ?? toLocalMidnight(new Date());
        goTo(dt);
      }
    }
  };

  return (
    <DateNavigator
      selectedDate={selectedDate}
      availableDates={matchDays}
      label={label}
      onPrev={onPrev}
      onNext={onNext}
      onDateSelect={onDayPress}
    />
  );
};

export default DateSelector;
